import { Role, User, UserRoleNodeMapping, UserRoleNodeMappingPayload } from '@ekkogmbh/apisdk';
import { Add, Close, Save } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import RolePicker from 'src/RoleManagement/RolePicker';
import UserPicker from 'src/UserManagement/UserPicker';

interface Props {
  /** When set, the panel is for adding mappings to this user — User picker is locked. */
  user?: User;
  /** When set, the panel is for adding mappings to this role — Role picker is locked. */
  role?: Role;
  /** When set, the panel edits an existing mapping. */
  mapping?: UserRoleNodeMapping;
  closeHandler: () => void;
  /** Called after a successful save (add or update) so the parent can refresh + close. */
  onSaved?: () => void;
}

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

const defaultValidUntil = (): string => moment().add(1, 'd').format('YYYY-MM-DD');

export const MappingsAdminPanel = ({ user, role, mapping, closeHandler, onSaved }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const [, startSaving] = useRequest();
  const isEdit = mapping !== undefined;

  const [selectedUser, setSelectedUser] = useState<User | undefined>(mapping?.user ?? user);
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(mapping?.role ?? role);
  const [coordinate, setCoordinate] = useState<string>(mapping?.node?.coordinate ?? '');
  const [validUntil, setValidUntil] = useState<string>(
    mapping?.validUntil ? moment(mapping.validUntil).format('YYYY-MM-DD') : defaultValidUntil(),
  );

  // Reset when props change (e.g. switching from Add to Edit without
  // unmounting the drawer between).
  useEffect(() => {
    setSelectedUser(mapping?.user ?? user);
    setSelectedRole(mapping?.role ?? role);
    setCoordinate(mapping?.node?.coordinate ?? '');
    setValidUntil(mapping?.validUntil ? moment(mapping.validUntil).format('YYYY-MM-DD') : defaultValidUntil());
  }, [user, role, mapping]);

  // Pre-fill coordinate from the role when the selected role's coordinate
  // doesn't already share a prefix with the current input. Mirrors legacy
  // UserMappingPanel behavior so existing flows feel the same.
  useEffect(() => {
    if (role === undefined) {
      return;
    }
    if (role.coordinate.startsWith(coordinate) || coordinate.startsWith(role.coordinate)) {
      return;
    }
    setCoordinate(role.coordinate);
    // Intentionally only reacts to `role` changes — local coordinate
    // edits shouldn't re-trigger this auto-fill.
  }, [role]);

  const isValid = useMemo(() => {
    if (selectedUser === undefined || selectedRole === undefined || coordinate === '') {
      return false;
    }
    if (!selectedUser.automation && validUntil === '') {
      return false;
    }
    return true;
  }, [selectedUser, selectedRole, coordinate, validUntil]);

  const onSave = (): void => {
    if (!selectedUser || !selectedRole) {
      return;
    }
    const payload: UserRoleNodeMappingPayload = {
      user: selectedUser,
      role: selectedRole,
      coordinate,
      validUntil,
    };
    startSaving(
      () => {
        if (isEdit && mapping !== undefined) {
          return api.updateUserRoleNodeMapping({ ...mapping, ...payload });
        }
        return api.addUserRoleNodeMapping(payload);
      },
      () => {
        onSaved?.();
        closeHandler();
      },
    );
  };

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={0}>
          <Typography variant="h6" fontWeight={700}>
            {isEdit ? 'Edit mapping' : 'Add mapping'}
          </Typography>
          {isEdit && selectedUser && selectedRole && (
            <Typography variant="caption" color="text.secondary">
              {selectedUser.username} · {selectedRole.name}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={closeHandler} size="small" aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
        <Stack spacing={4}>
          {/* Subject */}
          <Stack spacing={1.5}>
            <SectionHeader title="Subject" />
            <UserPicker disabled={user !== undefined} user={selectedUser} onChange={(u) => setSelectedUser(u)} />
            <RolePicker
              disabled={role !== undefined}
              role={selectedRole}
              onChange={(r) => setSelectedRole(r as Role | undefined)}
            />
          </Stack>

          {/* Scope */}
          <Stack spacing={1.5}>
            <SectionHeader title="Scope" />
            <CoordinateInput value={coordinate} onChange={setCoordinate} trailingDelimiter={false} />
          </Stack>

          {/* Validity */}
          {selectedUser && !selectedUser.automation && (
            <Stack spacing={1.5}>
              <SectionHeader title="Validity" />
              <DatePicker
                label="Valid until"
                disablePast
                value={moment(validUntil)}
                onChange={(date: Moment | null) => {
                  if (date !== null) {
                    setValidUntil(date.format('YYYY-MM-DD'));
                  }
                }}
                minDate={moment().add(1, 'd')}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button color="inherit" startIcon={<Close />} onClick={closeHandler}>
          Cancel
        </Button>
        <Button variant="contained" endIcon={isEdit ? <Save /> : <Add />} onClick={onSave} disabled={!isValid}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </Stack>
  );
};
